import React from 'react';
import {
    BodyTextMedium,
    Button,
    HeadingText40,
    Modal,
    pxToRem,
} from '@modernhealth/vital-components';
import { captureException } from 'src/shared/helpers/captureException';
import { useAnalytics } from '@modernhealth/web-analytics';
import { EventLexicon } from '@modernhealth/analytics-lexicon';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { toastNotificationsService } from 'src/config/rootService';
import { config } from 'src/config/config';

import { PathwayProviderFragment } from '../documents/fragments.generated';
import { useCancelMemberPathwayMutation } from '../documents/cancelMemberPathway.generated';

interface Props {
    pathwayUuid: string;
    provider: PathwayProviderFragment['provider'];
    pathway: string;
    onClose: () => void;
}

export const PathwayCancelModal: React.FC<Props> = ({
    pathway,
    pathwayUuid,
    provider,
    onClose,
}) => {
    const { t } = useTranslation();
    const { trackEvent } = useAnalytics();
    const [cancelMemberPathway] = useCancelMemberPathwayMutation();
    const handleCancelPathway = async () => {
        try {
            trackEvent(
                EventLexicon.clickEndPathwayConfirmation({
                    pathwayUuid,
                    providerUuid: provider.uuid,
                }),
            );
            await cancelMemberPathway({
                variables: {
                    cancelMemberPathwayMutationInput: {
                        pathwayUuid,
                    },
                },
            });
            toastNotificationsService.renderSuccessToast(
                t('pathways:cancel_pathway.success_toast', { provider: provider.firstName }),
            );
            onClose();
        } catch (error) {
            captureException(error);
            toastNotificationsService.renderErrorToast(config.messages.genericError);
        }
    };

    return (
        <Modal isVisible onCloseButtonClick={onClose} maxWidth={450}>
            <Wrapper>
                <HeadingText40 centered>
                    {t('pathways:cancel_pathway.title', {
                        pathwayTitle: pathway,
                        provider: `${provider.firstName} ${provider.lastName}`,
                    })}
                </HeadingText40>
                <BodyTextMedium marginTop={20} marginBottom={20} centered>
                    {t('pathways:cancel_pathway.subtitle', {
                        firstname: provider.firstName,
                    })}
                </BodyTextMedium>
                <Button fluid variant="destructive" onClick={handleCancelPathway} size="large">
                    {t('pathways:action.end_pathway')}
                </Button>
                <NotNowButton fluid variant="flat" onClick={onClose}>
                    {t('pathways:action.not_now')}
                </NotNowButton>
            </Wrapper>
        </Modal>
    );
};

const Wrapper = styled.div({
    padding: pxToRem(10),
});

const NotNowButton = styled(Button)({
    marginTop: '20px',
});
